import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { CAPTION_LIGHT_CLASS_NAME } from "tsi-common-react/src/utils/typography";
import { t } from "ttag";

import type {
    ImageChooserBlock,
    ReviewBlock,
    ReviewsBlock as ReviewsBlockValue,
} from "@reactivated";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { StarRating } from "../StarRating";

import styles from "./index.module.scss";

const rendition = (
    image: Immutable<ImageChooserBlock>,
    key: string,
): Immutable<ImageChooserBlock["renditions"][string]> | undefined =>
    image.renditions[key];

const imageSrc = (image: Immutable<ImageChooserBlock>, key: string): string =>
    rendition(image, key)?.url ?? image.imageData ?? image.url;

const richTextContents = (value: string | null | undefined): string =>
    (value ?? "")
        .replace(/^<div class="rich-text">/, "")
        .replace(/<\/div>$/, "");

const formatDate = (date: string | null | undefined): string => {
    if (!date) {
        return "";
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!match) {
        return date;
    }
    const parsed = new Date(
        Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
    );
    return parsed.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
};

const legacyGetFormProps = (
    action: string,
): React.FormHTMLAttributes<HTMLFormElement> & { type: string } => ({
    type: "GET",
    action,
});

const PullReviews = (
    props: Immutable<{
        value: ReviewsBlockValue;
        page_slug: string | null;
    }>,
): React.JSX.Element => {
    const value = props.value;
    return (
        <section className="reviews">
            <div
                className={concatClassNames([
                    "main-container content",
                    props.page_slug,
                ])}
            >
                <h1>{value.title}</h1>
                <div className="all-reviews">
                    {(value.reviews ?? []).map((review, i) => (
                        <div
                            key={i}
                            className={concatClassNames([
                                styles.pullReview,
                                "review",
                            ])}
                        >
                            <p>{review.title}</p>
                            <p className={styles.pullReviewName}>
                                {review.author}
                            </p>
                            <p className={styles.pullReviewLocation}>
                                {review.city}, {review.state}
                            </p>
                        </div>
                    ))}
                </div>

                <form {...legacyGetFormProps(value.read_all_link?.url ?? "")}>
                    <button className="button button--secondary">
                        {value.read_all_link?.title}
                    </button>
                </form>
            </div>
        </section>
    );
};

const FullReview = (
    props: Immutable<{ review: ReviewBlock; isProductPageBody?: boolean }>,
): React.JSX.Element => {
    const review = props.review;
    const { author, city, state } = review;
    return (
        <div className={concatClassNames([styles.fullReview, "review"])}>
            <StarRating
                rating={review.stars}
                inverted
                ratingClassName={styles.fullReviewStars}
                srLabel
            />
            <h3>{review.title}</h3>
            <p
                className={concatClassNames([
                    styles.byLine,
                    CAPTION_LIGHT_CLASS_NAME,
                ])}
            >
                {richT(t`by <strong>${author}</strong> in ${city}, ${state}`, {
                    strong: (children) => <strong>{children}</strong>,
                })}
            </p>
            <span
                className={concatClassNames([
                    styles.byLine,
                    CAPTION_LIGHT_CLASS_NAME,
                ])}
            >
                {formatDate(review.date)}
            </span>
            <p></p>
            <div
                className="rich-text"
                dangerouslySetInnerHTML={{
                    __html: richTextContents(review.review),
                }}
            />
            <p></p>
        </div>
    );
};

const FullReviews = (
    props: Immutable<{
        value: ReviewsBlockValue;
        isProductPageBody?: boolean;
    }>,
): React.JSX.Element => {
    const value = props.value;
    const icon = value.icon;
    const iconRendition = icon ? rendition(icon, "original") : undefined;
    return (
        <section className="reviews-full">
            <div className={styles.reviewsFullTitle}>
                <div
                    className={concatClassNames([
                        "full-title",
                        styles.fullTitle,
                    ])}
                >
                    {icon && (
                        <img
                            src={imageSrc(icon, "original")}
                            alt={icon.default_alt_text}
                            width={iconRendition?.width ?? icon.width}
                            height={iconRendition?.height ?? icon.height}
                            loading="lazy"
                            decoding="async"
                        />
                    )}
                    <h2>{value.title}</h2>
                </div>
            </div>
            <div
                className={concatClassNames([
                    styles.fullReviewsMain,
                    "main-container",
                ])}
            >
                <div className={styles.reviewsContainer}>
                    {(value.reviews ?? []).map((review, i) => (
                        <FullReview
                            key={i}
                            review={review}
                            isProductPageBody={props.isProductPageBody}
                        />
                    ))}
                </div>

                <form {...legacyGetFormProps(value.read_all_link?.url ?? "")}>
                    <button className="button button--secondary">
                        {value.read_all_link?.title}
                    </button>
                </form>
            </div>
        </section>
    );
};

const OwnerSayings = (
    props: Immutable<{ value: ReviewsBlockValue }>,
): React.JSX.Element => {
    const value = props.value;
    return (
        <section className={styles.reviewsOwnerSayings}>
            <div className="main-container">
                <div className={styles.reviewsOwnerSayingsTitle}>
                    {value.title}
                </div>
                <div className={styles.reviewsOwnerSayingsReviews}>
                    {(value.reviews ?? []).map((review, i) => (
                        <div
                            key={i}
                            className={styles.reviewsOwnerSayingsReview}
                        >
                            <h3 className={styles.reviewCopy}>
                                &quot;{review.title}&quot;
                            </h3>
                            <p className={styles.reviewsOwnerSayingsAuthor}>
                                {review.author}
                            </p>
                            <p className={styles.reviewsOwnerSayingsLocation}>
                                {review.city}, {review.state}
                            </p>
                        </div>
                    ))}
                </div>
                {value.read_all_link ? (
                    <div className={styles.reviewsOwnerSayingsLinkContainer}>
                        <a
                            href={value.read_all_link.url}
                            className={styles.reviewsOwnerSayingsLink}
                        >
                            {t`Read More Reviews`}
                        </a>
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export const CocoonReviewsBlock = (
    props: Immutable<{
        value: ReviewsBlockValue;
        page_slug: string | null;
        isProductPageBody?: boolean;
    }>,
): React.JSX.Element => {
    const value = props.value;
    return (
        <div
            className={concatClassNames([
                "review-block",
                getGlobalSpacingClasses(value.spacing),
            ])}
        >
            {value.style === "pull" ? (
                <PullReviews value={value} page_slug={props.page_slug} />
            ) : null}
            {value.style === "full" ? (
                <FullReviews
                    value={value}
                    isProductPageBody={props.isProductPageBody}
                />
            ) : null}
            {value.style === "owner_sayings" ? (
                <OwnerSayings value={value} />
            ) : null}
        </div>
    );
};
